<template>
    <RegularModal ref="modal">
        <template v-slot:title>{{ translate("page.admin_academy_results.add_rule") }}</template>
        <template v-slot:body>
            <div>
                <h5 class="is-size-5 mb-2">{{ translate("common.categories") }}</h5>
                <div class="mb-1">
                    <QueryInput :size="'is-small'" v-model="categoriesQuery" />
                </div>

                <SelectionList
                    ref="categoriesSelectionList"
                    :options="filteredCategories"
                    :multi-select="true"
                    @change="categoriesSelectionUpdated"
                    v-model="addRuleStore.selectedCategories"
                    :comparing-function="(a: Category, b: Category) => a.key === b.key"
                >
                    <template v-slot:option="{ option: category }">
                        <div class="option">
                            {{ category.value }}
                        </div>
                    </template>
                </SelectionList>

                <div class="buttons mt-2">
                    <button :disabled="inputDisabled" class="button is-small" @click="selectVisibleCategories">
                        {{ translate("page.admin_academy_results.select_visible") }}
                    </button>
                    <button :disabled="inputDisabled" class="button is-small" @click="deselectVisibleCategories">
                        {{ translate("page.admin_academy_results.unselect_visible") }}
                    </button>

                    <Checkbox2 class="mb-2" v-model="filterOutCategoriesUsedElsewhere">
                        {{ translate("page.admin_academy_results.show_only_categories_not_used_elsewhere") }}
                    </Checkbox2>
                </div>
            </div>
            <div class="mt-3">
                <h5 class="is-size-5 mb-2">{{ translate("page.admin_academy_results.select_rule") }}</h5>

                <RuleSelect />

                <RuleOptions />
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">{{ translate("common.cancel") }}</button>
                <LoadingButton :is-loading="confirmInProgress" class="button is-primary" @click="confirm" :disabled="!canRuleBeConfirmed">
                    {{ translate("common.save") }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import RegularModal from "../../Common/RegularModal.vue";
    import { useModal } from "../../Common/useModal";
    import { translate } from "../../Common/translations";
    import QueryInput from "../../Common/QueryInput.vue";
    import Checkbox2 from "../../Common/Checkbox2.vue";
    import { useAcademyResultsStore } from "../academyResultsStore";
    import { computed, ref } from "vue";
    import { Category } from "../../Common/types";
    import objectMatcher from "../../Common/objectMatcher";
    import categoriesApi from "../../Api/Categories/categories";
    import { handleError } from "../../Common/errorHandler";
    import RuleSelect from "./RuleSelect.vue";
    import RuleOptions from "./RuleOptions.vue";
    import { useAddRuleStore } from "./addRuleStore";
    import SelectionList from "../../Common/SelectionList.vue";
    import { eventBus, SELECTED_CATEGORIES_UPDATE } from "./eventBus";
    import { ComponentExposed } from "vue-component-type-helpers";
    import LoadingButton from "../../Common/LoadingButton.vue";

    const mainStore = useAcademyResultsStore();
    const addRuleStore = useAddRuleStore();
    const store = useAcademyResultsStore();
    const initDone = ref(false);

    /* CATEGORY SELECTOR */

    const categories = ref<Category[]>([]);
    const categoriesQuery = ref("");
    const inputDisabled = computed(() => store.publishingMode);
    const filterOutCategoriesUsedElsewhere = ref(true);

    const filteredCategories = computed(() =>
        categories.value.filter(category => {
            if (!objectMatcher.matchString(categoriesQuery.value, category.value)) {
                return false;
            }

            if (filterOutCategoriesUsedElsewhere.value) {
                return !mainStore.isCategorySelectedInSelectedRules(category.key);
            }

            return true;
        }),
    );

    const categoriesSelectionList = ref<ComponentExposed<typeof SelectionList> | null>(null);

    function selectVisibleCategories() {
        categoriesSelectionList.value?.selectAllVisible();
    }
    function deselectVisibleCategories() {
        categoriesSelectionList.value?.deselectAllVisible();
    }

    const fetchCategories = () => {
        categories.value = [];
        return categoriesApi.getCategories(store.eventId).then(result => {
            categories.value = result;
        });
    };

    function categoriesSelectionUpdated() {
        eventBus.$emit(SELECTED_CATEGORIES_UPDATE);
    }

    /* SAVING SELECTED RULE TO MAIN STORE */

    const canRuleBeConfirmed = computed(() => {
        if (!addRuleStore.selectedRule || !addRuleStore.validRule) {
            return false;
        }

        return addRuleStore.selectedCategoriesArray.length > 0;
    });

    const confirmInProgress = ref(false);

    function confirm() {
        if (!canRuleBeConfirmed.value || !addRuleStore.selectedRule) {
            return;
        }

        confirmInProgress.value = true;

        store.addSelectedRule(addRuleStore.selectedRule);

        return store
            .fetchAcademyResults()
            .then(() => {
                close();
            })
            .finally(() => {
                confirmInProgress.value = false;
            })
            .catch(handleError);
    }

    /* MODAL HANDLING */

    const { modal, open: openModal, close } = useModal();

    function open() {
        categoriesQuery.value = "";
        addRuleStore.resetToDefaults();
        addRuleStore.eventId = store.eventId;
        initDone.value = false;
        openModal();

        return fetchCategories()
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    }

    defineExpose({ open });
</script>

<style scoped></style>
